















import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import ItemList from '~/components/Games/Items/ItemList.vue';
import { IGameItemForSaleByGala } from '~/store/games/types';
import getItemsForSaleByGala from '~/queries/storeItems.gql';
import { filterList } from '~/utils/listFilters';
import { State } from 'vuex-class';

@Component({
  components: {
    ItemList,
  },
})
export default class SimilarItems extends Vue {
  @State(profile => profile.user.goldMember, { namespace: 'profile' })
  readonly goldMember!: boolean;
  @State(shareState => shareState.founderNodesOwned, { namespace: 'share' })
  readonly founderNodesOwned!: number;

  @Prop() item!: IGameItemForSaleByGala;

  fetchingItemsForSale = false;
  filteredItems: IGameItemForSaleByGala[] = [];
  itemsForSaleByGala: IGameItemForSaleByGala[] = [];

  get filteredItemsForSale() {
    if (!this.goldMember && !this.founderNodesOwned) {
      return filterList('userNotGoldOrNodeOwner', this.itemsForSaleByGala);
    } else if (!this.goldMember) {
      return filterList('goldMember', this.itemsForSaleByGala);
    } else if (!this.founderNodesOwned) {
      return filterList('userNotNodeOwner', this.itemsForSaleByGala);
    } else {
      return this.itemsForSaleByGala;
    }
  }

  get similarItems() {
    if (
      this.item.name === 'Gala Node License' ||
      this.item.name === 'Gala Gold Membership'
    ) {
      return [];
    }

    const basicFilteredItems = this.filteredItemsForSale.filter(val => {
      // Filter out the current displayed item, sold out items, and items with purchasing disabled
      return val.baseId !== this.item.baseId && val.purchasingDisabled !== true;
    });

    if (basicFilteredItems.length <= 5) {
      return basicFilteredItems;
    }

    if (this.item.category) {
      const filteredByCategory = basicFilteredItems.filter(val => {
        return val.category === this.item.category;
      });

      if (filteredByCategory.length === 5) {
        return filteredByCategory;
      } else if (filteredByCategory.length < 5) {
        const itemsToAddIn = 5 - filteredByCategory.length;
        const closestItems = this.getClosestPricedItemsInArray(
          itemsToAddIn,
          basicFilteredItems.filter(val => {
            return val.category !== this.item.category;
          }),
        );

        return [...filteredByCategory, ...closestItems];
      } else {
        return this.getClosestPricedItemsInArray(5, filteredByCategory);
      }
    } else {
      return this.getClosestPricedItemsInArray(5, basicFilteredItems);
    }
  }

  async created() {
    try {
      this.fetchingItemsForSale = true;
      const storeItemsInput = {
        currency:
          this.item && this.item.prices.length === 1
            ? this.item.prices[0].symbol
            : 'GALA',
        numberFilterOptions: [
          {
            field: 'supply.currentSupply',
            value: 0,
            comparator: 'gt',
          },
        ],
        stringFilterOptions: [
          {
            field: 'game',
            value: this.item.game,
            comparator: 'eq',
          },
        ],
      };
      const results = (await this.$apollo.query({
        query: getItemsForSaleByGala,
        variables: {
          storeItemsInput,
        },
        fetchPolicy: 'no-cache',
      })) as { data: { storeItems: any[] } };
      this.fetchingItemsForSale = false;
      if (results && results.data && results.data.storeItems) {
        this.itemsForSaleByGala = results.data.storeItems;
      }
    } catch (error) {
      this.$sentry.captureException(error);
      this.fetchingItemsForSale = false;
    }
  }

  getClosestPricedItemsInArray(
    numberToAddIn: number,
    items: IGameItemForSaleByGala[],
  ) {
    const viewedItemPrice = this.getGalaPrice(this.item);
    const closestItems = [];

    if (!items[0]) {
      return [];
    }

    // If the currently viewed item doesn't have a GALA price, simply return the first items in the given array
    if (!viewedItemPrice) {
      return items.slice(0, numberToAddIn);
    }

    let itemList = items.slice();

    for (let i = 0; i < numberToAddIn; i++) {
      let closestItem = itemList.reduce(
        (
          previousValue: IGameItemForSaleByGala,
          currentValue: IGameItemForSaleByGala,
        ): IGameItemForSaleByGala => {
          const prevPrice = this.getGalaPrice(previousValue);
          const currentPrice = this.getGalaPrice(currentValue);

          if (prevPrice && currentPrice) {
            if (
              Math.abs(currentPrice - viewedItemPrice) <
              Math.abs(prevPrice - viewedItemPrice)
            ) {
              return currentValue;
            } else {
              return previousValue;
            }
          } else if (prevPrice) {
            // If the previous item has a GALA price value but the current item does not, return the previous item
            return previousValue;
          }

          // If there isn't a valid GALA price value for comparison, simply return the current value
          return currentValue;
        },
      );

      itemList = itemList.filter(val => {
        return val.baseId !== closestItem.baseId;
      });

      closestItems.push(closestItem);
    }

    return closestItems;
  }

  getGalaPrice(item: IGameItemForSaleByGala) {
    if (item.prices && item.prices[0]) {
      const baseCurrencyPrice =
        item && item.prices.length === 1 ? item.prices[0].symbol : 'GALA';
      const galaPriceInfo = item.prices.filter(val => {
        return val.symbol === baseCurrencyPrice;
      });

      if (galaPriceInfo[0]) {
        return parseFloat(galaPriceInfo[0].price);
      }
    }
  }
}
